import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Upload,
  Download,
  FileJson,
  FileCode,
  Mail,
  Lock,
  Send,
} from 'lucide-react';
import { api } from '../hooks/api.js';
import {
  emailDocumentFromRow,
  type EmailStatus,
  EMAIL_STATUS_FILTERS,
  slugify,
} from '@blogforge/shared';
import { ImportEmailModal } from '../components/ImportEmailModal.js';
import { useCreateGuard, PlanUpgradeNotice } from '../components/PlanGuard.js';

const STATUS_FILTERS = EMAIL_STATUS_FILTERS;

function downloadFile(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Export the saved email as JSON.
 *
 * The whole document, not a hand-picked subset of columns: this function used to
 * build its own four-field object, so an exported email imported back lost its
 * `variableDefinition` (and anything else the list had not been updated for).
 * `emailDocumentFromRow` reads the same schema the import and the create
 * endpoint speak, so the three cannot disagree about what an email is.
 */
function exportEmailJson(email: Record<string, unknown>) {
  const document = emailDocumentFromRow(email);
  downloadFile(
    `${slugify(document.subject) || 'email'}.json`,
    JSON.stringify(document, null, 2),
    'application/json',
  );
}

/** Export the built email as a standalone HTML file. */
async function exportEmailHtml(email: Record<string, unknown>) {
  const result = (await api.emails.build(email['id'] as string)) as {
    slug?: string;
    html?: string;
  };
  if (!result.html) throw new Error('Build returned no HTML');
  downloadFile(
    `${result.slug ?? (slugify(email['subject'] as string) || 'email')}.html`,
    result.html,
    'text/html',
  );
}

export function EmailsPage() {
  const navigate = useNavigate();
  const emailGuard = useCreateGuard('email');
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<EmailStatus | 'all'>('all');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [openExportId, setOpenExportId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['emails', { search, status: statusFilter }],
    queryFn: () =>
      api.emails.list({
        search: search || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 30,
      }),
  });

  const createMutation = useMutation({
    mutationFn: () => api.emails.create({ subject: 'Untitled email' }),
    onSuccess: (email: unknown) => {
      void qc.invalidateQueries({ queryKey: ['emails'] });
      navigate(`/emails/${(email as { id: string }).id}/edit`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.emails.delete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['emails'] }),
  });

  const emails = (data as { data: unknown[] } | undefined)?.data ?? [];

  const handleExportJson = (email: Record<string, unknown>) => {
    try {
      exportEmailJson(email);
    } catch (err) {
      window.alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleExportHtml = async (email: Record<string, unknown>) => {
    try {
      await exportEmailHtml(email);
    } catch (err) {
      window.alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Page header ─────────────────────────────────────── */}
      <header className="relative shrink-0 px-8 pt-8 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="kicker mb-2">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              Outreach
            </p>
            <h1 className="text-[22px] font-bold tracking-tight text-ink">Emails</h1>
            <p className="mt-1 text-[13px] text-ink-soft">
              Design on-brand emails from the same component blocks.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsImportModalOpen(true)}
              type="button"
              className="btn-secondary"
              title="Import JSON as a new email"
            >
              <Upload size={14} />
              Import
            </button>
            <button
              onClick={() => !emailGuard.allowed || createMutation.mutate()}
              disabled={createMutation.isPending}
              title={emailGuard.blockedReason}
              type="button"
              className="btn-primary"
            >
              {emailGuard.allowed ? (
                <>
                  <Plus size={15} strokeWidth={2.4} />
                  New email
                </>
              ) : (
                <>
                  <Lock size={14} />
                  Individual required
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Plan feature notice ───────────────────────────────── */}
      {!emailGuard.allowed && emailGuard.blockedReason && (
        <div className="shrink-0 px-8 pb-4">
          <PlanUpgradeNotice reason={emailGuard.blockedReason} />
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="shrink-0 px-8 pb-5">
        <div className="surface-row flex flex-wrap items-center gap-3 px-3.5 py-3">
          <div className="input-with-icon min-w-52 max-w-md flex-1">
            <Search />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search emails…"
              className="field-input bg-bg/60"
            />
          </div>
          <div className="ml-auto flex max-w-full flex-wrap items-center gap-1 rounded-full border border-line bg-bg/60 p-1">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-all duration-150 ${
                  statusFilter === s
                    ? 'bg-card text-ink shadow-sm'
                    : 'text-muted hover:text-ink-soft'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Email list ──────────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-10">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center gap-2 text-sm text-muted">
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
            Loading emails…
          </div>
        ) : emails.length === 0 ? (
          <div className="flex h-full min-h-72 flex-col items-center justify-center text-center">
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
              <Mail size={26} strokeWidth={1.8} />
            </div>
            <h2 className="text-base font-semibold text-ink">No emails here yet</h2>
            <p className="mt-1 max-w-xs text-[13px] text-muted">
              Build one from blocks — or import an existing design as JSON.
            </p>
            <div className="mt-5 flex items-center gap-2">
              <button onClick={() => setIsImportModalOpen(true)} type="button" className="btn-secondary">
                <Upload size={14} />
                Import from JSON
              </button>
              <button onClick={() => createMutation.mutate()} type="button" className="btn-primary">
                <Plus size={15} strokeWidth={2.4} />
                Create your first email
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {(emails as Record<string, unknown>[]).map((email) => (
              <div
                key={email['id'] as string}
                className="group surface-row flex items-center gap-4 px-4 py-3.5 transition-all duration-150 hover:border-accent-border hover:shadow-glow"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bg text-muted transition-colors group-hover:text-accent">
                  <Mail size={15} />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">
                    {email['subject'] as string}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className={`badge badge-${email['status'] as string}`}>
                      {email['status'] as string}
                    </span>
                    {email['previewText'] ? (
                      <span className="truncate text-xs text-muted">
                        {(email['previewText'] as string).slice(0, 90)}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  {/* Export dropdown */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenExportId(
                          openExportId === email['id'] ? null : (email['id'] as string),
                        )
                      }
                      className="btn-ghost px-2.5 text-xs"
                      title="Export"
                    >
                      <Download size={13} />
                      Export
                    </button>
                    {openExportId === email['id'] && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setOpenExportId(null)} />
                        <div className="absolute right-0 z-50 mt-1.5 w-44 overflow-hidden rounded-xl border border-line bg-card p-1 shadow-lift">
                          <button
                            type="button"
                            onClick={() => {
                              setOpenExportId(null);
                              handleExportJson(email);
                            }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-ink-soft hover:bg-ink/5 hover:text-ink"
                          >
                            <FileJson size={13} className="text-muted" />
                            Export JSON
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setOpenExportId(null);
                              void handleExportHtml(email);
                            }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-ink-soft hover:bg-ink/5 hover:text-ink"
                          >
                            <FileCode size={13} className="text-muted" />
                            Export HTML
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate(`/emails/send?emailId=${email['id'] as string}`)}
                    className="btn-ghost px-2.5 text-xs"
                    title="Queue this email to an audience"
                  >
                    <Send size={13} />
                    Send
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/emails/${email['id'] as string}/edit`)}
                    className="btn-ghost px-2.5 text-xs"
                  >
                    <Pencil size={13} />
                    Edit
                  </button>
                  <button
                    type="button"
                    title="Archive Email"
                    onClick={() => {
                      if (confirm('Archive this email?')) {
                        deleteMutation.mutate(email['id'] as string);
                      }
                    }}
                    className="btn-ghost px-2.5 text-xs text-danger hover:bg-danger/10 hover:text-danger"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Import modal */}
      {isImportModalOpen && <ImportEmailModal onClose={() => setIsImportModalOpen(false)} />}
    </div>
  );
}
